import { useState, useRef, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import Avvvatars from "avvvatars-react";
import ClientAvatar from "@components/chat/ClientAvatar";
import Icon1 from "@assets/icons/Icon";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import ChatPanel from "@components/chat/ChatPanel";
import FloatingChatWidget from "@components/chat/FloatingChatWidget";
import SearchField from "@components/ui/textfields/SearchField";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import DatePicker from "@components/ui/selection/DatePicker";
import MenuButton from "@components/ui/buttons/MenuButton";
import SideMenuButton from "@navigation/SideMenuButton";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { Text1 } from "@components/ui/texts/Texts";
import { DocumentPreviewModal } from "@components/ui/DocumentPreviewModal";
import { getDossierById, useDossierStore } from "@store/dossierStore";
import { useDataStore } from "@store/user_org";
import { useTagStore } from "@store/tagStore";
import InlineTagEditor from "@components/ui/tags/InlineTagEditor";
import { dossiersApi, type DocumentClassification } from "@services/api/dossiers";
import { clientVehiclesApi } from "@services/api/clientVehicles";
import { ordresApi } from "@services/api/ordres";
import { searchDossiersFull, type ProjectHit } from "@services/algolia";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import BaseModal from "@components/modals/BaseModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import Button from "@components/ui/buttons/Button";
import type {
  Dossier,
  DossierTimelineEvent,
  DossierDocument,
  DossierOrdreSummary,
  ClientNote,
} from "types/dossier";
import type { Contact } from "types/contact";
import type { ClientVehicle } from "types/client_vehicle";
import type { Ordre, OrdreDocument } from "types/ordre";

import VehicleImage from "@components/ui/VehicleImage";
import { CLIENT_TYPE_BADGE } from "@constants/clientTypes";
import VehicleEditModal from "@components/modals/VehicleEditModal";
import ServiceBoxTransferModal from "@components/modals/ServiceBoxTransferModal";
import ContactFormModal from "@components/modals/ContactFormModal";
import DossierEditModal from "@components/modals/DossierEditModal";
import ClientNotesBanner from "@components/ui/ClientNotesBanner";

// ─── Constants ─────────────────────────────────────────────

// ─── Mock detail data per dossier ──────────────────────────

const MOCK_DETAIL: Record<string, Partial<Dossier>> = {
  "dos-1": {
    contacts: [
      { id: "c1", dossier_id: "dos-1", nom: "DESCHAMPS PATRICK & MAISON", prenom: "François", civilite: "M.", pays: "FRANCE", phone: "06 08 97 77 14", email: "francois@maison.fr", is_primary: true, created_at: "2025-01-01T00:00:00Z" },
      { id: "c2", dossier_id: "dos-1", nom: "MARTIN", prenom: "Michel", civilite: "M.", pays: "FRANCE", phone: "06 98 76 54 32", is_primary: false, created_at: "2025-01-01T00:00:00Z" },
    ],
    vehicles: [
      { id: "v1", org_id: "org1", dossier_id: "dos-1", plate: "EE-249-GR", marque: "PEUGEOT", modele: "5008", coloris: "blanc", km: 77025, vin: "WF0DXXGAKDGC01845", mec: 1785340800, created_at: "2025-01-01T00:00:00Z" },
      { id: "v2", org_id: "org1", dossier_id: "dos-1", plate: "FF-350-HS", marque: "PEUGEOT", modele: "508", coloris: "noir", km: 45200, created_at: "2025-01-01T00:00:00Z" },
    ],
    documents: [
      { id: "d1", name: "Vue en plan", type: "pdf", created_at: "18/12/2025" },
      { id: "d2", name: "Façades", type: "pdf", created_at: "18/12/2025" },
      { id: "d3", name: "Perspectives", type: "pdf", created_at: "18/12/2025" },
      { id: "d4", name: "Fondations", type: "pdf", created_at: "18/12/2025" },
      { id: "d5", name: "Carte grise", type: "pdf", created_at: "14/12/2025" },
      { id: "d6", name: "Assurance", type: "pdf", created_at: "14/12/2025" },
    ],
    timeline: [
      { id: "ev1", date: "2025-12-18T09:54:00Z", user_name: "Freddy", user_color: "#10b981", action: "a ajouté 4 documents", type: "documents", document_names: ["Vue en plan", "Façades", "Perspectives", "Fondations"] },
      { id: "ev2", date: "2025-12-15T10:30:00Z", user_name: "Freddy", user_color: "#10b981", action: "a ajouté 3 photos", type: "photos", photo_urls: ["https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=200&h=150&fit=crop", "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=200&h=150&fit=crop", "https://images.unsplash.com/photo-1541899481282-d53bffe3c35d?w=200&h=150&fit=crop"] },
      { id: "ev3", date: "2025-12-14T08:10:00Z", user_name: "Lana", user_color: "#8b5cf6", action: "a changé le status de l'OR IVSM48", type: "status_change", status_label: "Passé en Commande", status_type: "success", ordre_ref: "OR IVSM48" },
      { id: "ev4", date: "2025-12-14T08:02:00Z", user_name: "M. Martin", user_color: "#6366f1", action: "a ajouté 2 documents", type: "documents", document_names: ["Carte grise", "Assurance"] },
    ],
    ordres: [
      { id: "o1", num: "URSOCK1", type: "MECA", vehicle_name: "Véhicule 1", status: "en prépa.", status_color: "#A1A1A1", created_at: "02/03/2026 14:00", updated_at: "02/03/2026 16:00" },
      { id: "o2", num: "URSPOCK", type: "MECA", vehicle_name: "Véhicule 2", status: "en cours", status_color: "#3b82f6", created_at: "02/03/2026 14:00", updated_at: "02/03/2026 16:00" },
      { id: "o3", num: "APM54FT", type: "MECA", vehicle_name: "Véhicule 1", status: "restitué", status_color: "#10b981", created_at: "02/03/2026 14:00", updated_at: "02/03/2026 16:00" },
    ],
  },
  "dos-2": {
    contacts: [
      { id: "c3", dossier_id: "dos-2", nom: "MARTIN", prenom: "Sophie", civilite: "Mme", pays: "FRANCE", phone: "06 98 76 54 32", email: "s.martin@email.com", is_primary: true, created_at: "2025-01-01T00:00:00Z" },
    ],
    vehicles: [
      { id: "v3", org_id: "org1", dossier_id: "dos-2", plate: "GH-789-IJ", marque: "RENAULT", modele: "CLIO", coloris: "rouge", km: 62000, created_at: "2025-01-01T00:00:00Z" },
    ],
    documents: [
      { id: "d7", name: "Carte grise", type: "pdf", created_at: "15/12/2025" },
      { id: "d8", name: "Devis réparation", type: "pdf", created_at: "16/12/2025" },
    ],
    timeline: [
      { id: "ev5", date: "2025-12-17T14:20:00Z", user_name: "Marc", user_color: "#f59e0b", action: "a créé un devis", type: "status_change", status_label: "Devis envoyé", status_type: "info" },
      { id: "ev6", date: "2025-12-15T09:00:00Z", user_name: "Sophie Martin", user_color: "#6366f1", action: "a ajouté 1 document", type: "documents", document_names: ["Carte grise"] },
    ],
    ordres: [
      { id: "o4", num: "OR-2024-002", type: "MECA", vehicle_name: "Véhicule 1", status: "devis_supp", status_color: "#f59e0b", created_at: "15/12/2025 09:00", updated_at: "17/12/2025 14:20" },
    ],
  },
  "dos-3": {
    contacts: [
      { id: "c4", dossier_id: "dos-3", nom: "LEROY", prenom: "Thomas", civilite: "M.", pays: "FRANCE", phone: "01 42 33 44 55", email: "t.leroy@garagecentral.fr", is_primary: true, created_at: "2025-01-01T00:00:00Z" },
      { id: "c5", dossier_id: "dos-3", nom: "RENARD", prenom: "Julie", civilite: "Mme", pays: "FRANCE", email: "compta@garagecentral.fr", is_primary: false, created_at: "2025-01-01T00:00:00Z" },
    ],
    vehicles: [
      { id: "v4", org_id: "org1", dossier_id: "dos-3", plate: "KL-012-MN", marque: "PEUGEOT", modele: "308", coloris: "blanc", km: 55000, created_at: "2025-01-01T00:00:00Z" },
      { id: "v5", org_id: "org1", dossier_id: "dos-3", plate: "OP-345-QR", marque: "CITROEN", modele: "BERLINGO VP", coloris: "gris", km: 89000, created_at: "2025-01-01T00:00:00Z" },
      { id: "v6", org_id: "org1", dossier_id: "dos-3", plate: "ST-678-UV", marque: "RENAULT", modele: "KANGOO", coloris: "blanc", km: 120000, created_at: "2025-01-01T00:00:00Z" },
    ],
    documents: [
      { id: "d9", name: "Contrat leasing", type: "pdf", created_at: "10/12/2025" },
      { id: "d10", name: "Facture entretien #1204", type: "pdf", created_at: "14/12/2025" },
      { id: "d11", name: "PV contrôle technique", type: "pdf", created_at: "16/12/2025" },
    ],
    timeline: [
      { id: "ev7", date: "2025-12-16T11:30:00Z", user_name: "Lana", user_color: "#8b5cf6", action: "a ajouté le PV contrôle technique", type: "documents", document_names: ["PV contrôle technique"] },
      { id: "ev8", date: "2025-12-14T16:45:00Z", user_name: "Marc", user_color: "#f59e0b", action: "a terminé l'entretien du Berlingo", type: "status_change", status_label: "Entretien terminé", status_type: "success" },
      { id: "ev9", date: "2025-12-10T08:00:00Z", user_name: "Thomas Leroy", user_color: "#10b981", action: "a déposé le contrat leasing", type: "documents", document_names: ["Contrat leasing"] },
    ],
    ordres: [
      { id: "o5", num: "OR-FL-001", type: "MECA", vehicle_name: "Véhicule 2", status: "en cours", status_color: "#3b82f6", created_at: "10/12/2025 08:00", updated_at: "14/12/2025 16:45" },
    ],
  },
  "dos-4": {
    contacts: [
      { id: "c6", dossier_id: "dos-4", nom: "DUBOIS", prenom: "Pierre", civilite: "M.", pays: "FRANCE", phone: "07 11 22 33 44", is_primary: true, created_at: "2025-01-01T00:00:00Z" },
    ],
    vehicles: [
      { id: "v7", org_id: "org1", dossier_id: "dos-4", plate: "WX-901-YZ", marque: "VOLKSWAGEN", modele: "GOLF", coloris: "noir", km: 38000, created_at: "2025-01-01T00:00:00Z" },
    ],
    documents: [
      { id: "d12", name: "Constat amiable", type: "pdf", created_at: "12/12/2025" },
    ],
    timeline: [
      { id: "ev10", date: "2025-12-15T10:00:00Z", user_name: "Pierre Dubois", user_color: "#ef4444", action: "a demandé un suivi de réclamation", type: "comment" },
      { id: "ev11", date: "2025-12-12T14:30:00Z", user_name: "Freddy", user_color: "#10b981", action: "a ajouté 2 photos", type: "photos", photo_urls: ["https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=200&h=150&fit=crop", "https://images.unsplash.com/photo-1549317661-bd32c8ce0afa?w=200&h=150&fit=crop"] },
      { id: "ev12", date: "2025-12-12T09:15:00Z", user_name: "Pierre Dubois", user_color: "#ef4444", action: "a ajouté le constat amiable", type: "documents", document_names: ["Constat amiable"] },
    ],
    ordres: [],
  },
  "dos-5": {
    contacts: [
      { id: "c7", dossier_id: "dos-5", nom: "LEVY", prenom: "David", civilite: "M.", pays: "FRANCE", phone: "01 55 66 77 88", email: "d.levy@levy-transport.fr", is_primary: true, created_at: "2025-01-01T00:00:00Z" },
      { id: "c8", dossier_id: "dos-5", nom: "COHEN", prenom: "Sarah", civilite: "Mme", pays: "FRANCE", phone: "01 55 66 77 89", email: "flotte@levy-transport.fr", is_primary: false, created_at: "2025-01-01T00:00:00Z" },
    ],
    vehicles: [
      { id: "v8", org_id: "org1", dossier_id: "dos-5", plate: "AA-111-BB", marque: "RENAULT", modele: "KANGOO", coloris: "blanc", km: 95000, created_at: "2025-01-01T00:00:00Z" },
      { id: "v9", org_id: "org1", dossier_id: "dos-5", plate: "CC-222-DD", marque: "PEUGEOT", modele: "BIPPER", coloris: "blanc", km: 110000, created_at: "2025-01-01T00:00:00Z" },
    ],
    documents: [
      { id: "d14", name: "Bon de commande pièces", type: "pdf", created_at: "13/12/2025" },
    ],
    timeline: [
      { id: "ev13", date: "2025-12-14T09:00:00Z", user_name: "Marc", user_color: "#f59e0b", action: "a commandé les pièces de rechange", type: "status_change", status_label: "Pièces commandées", status_type: "info" },
      { id: "ev14", date: "2025-12-13T15:00:00Z", user_name: "Sarah Cohen", user_color: "#10b981", action: "a déposé le bon de commande", type: "documents", document_names: ["Bon de commande pièces"] },
    ],
    ordres: [
      { id: "o6", num: "OR-TR-001", type: "MECA", vehicle_name: "Véhicule 1", status: "en prépa.", status_color: "#A1A1A1", created_at: "13/12/2025 15:00", updated_at: "14/12/2025 09:00" },
    ],
  },
};

function getEnrichedDossier(id: string): Dossier | null {
  const base = getDossierById(id);
  if (!base) return null;
  const detail = MOCK_DETAIL[id];
  return {
    ...base,
    contacts: base.contacts?.length ? base.contacts : detail?.contacts || [],
    vehicles: base.vehicles?.length ? base.vehicles : detail?.vehicles || [],
    documents: base.documents?.length ? base.documents : detail?.documents || [],
    timeline: base.timeline?.length ? base.timeline : detail?.timeline || [],
    ordres: base.ordres?.length ? base.ordres : detail?.ordres || [],
  };
}

// ─── Mock chat messages ────────────────────────────────────


// ─── Timeline component ────────────────────────────────────

function formatEventDate(iso: string): { text: string; color?: string } {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const eventDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  if (eventDay.getTime() === today.getTime()) {
    return { text: `Auj. ${time}`, color: "#10b981" };
  }
  if (eventDay.getTime() === yesterday.getTime()) {
    return { text: `Hier ${time}`, color: "#171717" };
  }
  return { text: d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) + " " + time };
}

const IMAGE_NAME_RE = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

interface AllDocEntry { id: string; name: string; gcs_path?: string; parentType: "dossier" | "ordre"; parentId: string }

function DossierTimelineDocThumbnail({ docName, allDocs }: { docName: string; allDocs: AllDocEntry[] }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const doc = allDocs.find((d) => d.name === docName);
    if (!doc || !doc.gcs_path) return;
    let cancelled = false;
    const fetchUrl = doc.parentType === "ordre"
      ? ordresApi.getDocumentUrl(doc.parentId, doc.id)
      : dossiersApi.getDocumentUrl(doc.parentId, doc.id);
    fetchUrl.then((u) => { if (!cancelled) setUrl(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [docName, allDocs]);

  return (
    <div className="w-[80px] h-[80px] rounded-lg overflow-hidden bg-neutral-100 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0">
      {url ? (
        <img src={url} alt={docName} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Icon1 name="image" size={24} color="#D4D4D4" />
        </div>
      )}
    </div>
  );
}

function TimelineEvent({ event, isLast, onDocClick, allDocs }: { event: DossierTimelineEvent; isLast: boolean; onDocClick?: (docName: string) => void; allDocs?: AllDocEntry[] }) {
  const imageNames = (event.type === "documents" && event.document_names)
    ? event.document_names.filter((n) => IMAGE_NAME_RE.test(n))
    : [];
  const fileNames = (event.type === "documents" && event.document_names)
    ? event.document_names.filter((n) => !IMAGE_NAME_RE.test(n))
    : [];

  return (
    <div className="flex flex-row">
      <div className="flex-shrink-0 text-h3 font-medium" style={{ width: 90, paddingTop: 2, textAlign: "right", paddingRight: 12, color: formatEventDate(event.date).color || "#a3a3a3" }}>
        {formatEventDate(event.date).text}
      </div>
      <div className="flex flex-col items-center flex-shrink-0" style={{ width: 20 }}>
        <div className="rounded-full mt-1.5 flex-shrink-0" style={{ width: 10, height: 10, background: "#A3A3A3" }} />
        {!isLast && <div className="w-0.5 bg-neutral-300 flex-1 my-1" />}
      </div>
      <div className="flex flex-col gap-2 ml-3 flex-1 pb-6" style={{ minWidth: 0 }}>
        <div className="flex flex-row gap-2 items-start">
          <div className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999 }}>
            {event.sender_type === "client" ? (
              <ClientAvatar size={32} />
            ) : (
              <Avvvatars value={event.user_name} style="shape" size={32} />
            )}
          </div>
          <div className="flex flex-col">
            <span className="text-h2 font-semibold" style={{ lineHeight: "18px" }}>{event.user_name}</span>
            <span className="text-h3 text-neutral-400">{event.action}</span>
          </div>
        </div>
        {imageNames.length > 0 && allDocs && (
          <div className="flex flex-row gap-2 mt-1 flex-wrap">
            {imageNames.map((name, i) => (
              <DossierTimelineDocThumbnail key={i} docName={name} allDocs={allDocs} />
            ))}
          </div>
        )}
        {fileNames.length > 0 && (
          <div className="flex flex-col gap-0.5 mt-1">
            {fileNames.map((name, i) => (
              <div
                key={i}
                className="flex flex-row items-center gap-2 cursor-pointer hover:bg-neutral-50 rounded px-1 py-0.5"
                onClick={() => onDocClick?.(name)}
              >
                <Icon1 name="file" size={14} color="#6366f1" />
                <span className="text-h3 text-indigo-600 hover:underline">{name}</span>
              </div>
            ))}
          </div>
        )}
        {event.type === "photos" && event.photo_urls && (
          <div className="flex flex-row gap-2 mt-1 flex-wrap">
            {event.photo_urls.map((url, i) => (
              <div key={i} className="w-[80px] h-[80px] rounded-lg overflow-hidden bg-neutral-100 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0">
                <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        )}
        {event.type === "status_change" && event.status_label && (
          <div className="flex flex-row items-center gap-2 mt-1">
            <span className="text-h3 font-medium" style={{ color: event.status_type === "success" ? "#10b981" : event.status_type === "warning" ? "#f59e0b" : "#525252" }}>
              {event.status_label}
            </span>
            <Icon1 name={event.status_type === "success" ? "done" : "horloge"} size={16} color={event.status_type === "success" ? "#10b981" : "#525252"} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Fil du dossier tab ────────────────────────────────────

function FilDuDossierTab({ timeline, vehicles, search, onDocClick, allDocs }: { timeline: DossierTimelineEvent[]; vehicles: ClientVehicle[]; search: string; onDocClick?: (docName: string) => void; allDocs?: AllDocEntry[] }) {
  const [vehicleFilter, setVehicleFilter] = useState<string>("all");
  const filtered = timeline.filter((ev) => {
    if (vehicleFilter !== "all") {
      if (!ev.vehicle_id || ev.vehicle_id !== vehicleFilter) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      return ev.user_name.toLowerCase().includes(q) || ev.action.toLowerCase().includes(q) || (ev.document_names || []).some((n) => n.toLowerCase().includes(q)) || (ev.status_label || "").toLowerCase().includes(q);
    }
    return true;
  });
  const sorted = [...filtered].sort((a, b) => (a.date < b.date ? 1 : -1));

  const vehicleOptions = [
    { value: "all", label: "Tous les véhicules" },
    ...vehicles.map((v) => ({ value: v.id, label: `${v.marque} ${v.modele}` })),
  ];

  return (
    <div className="flex flex-col gap-3">
      {vehicles.length > 1 && (
        <div style={{ maxWidth: 250 }}>
          <DropDown
            options={vehicleOptions}
            value={vehicleFilter}
            onSelect={(val) => setVehicleFilter(String(val))}
            placeholder="Filtrer par véhicule"
          />
        </div>
      )}
      <div className="flex flex-col">
        {sorted.length === 0 ? (
          <div className="text-h3 text-center text-neutral-400 py-8">{search ? "Aucun résultat" : "Aucun événement"}</div>
        ) : sorted.map((ev, i) => <TimelineEvent key={ev.id} event={ev} isLast={i === sorted.length - 1} onDocClick={onDocClick} allDocs={allDocs} />)}
      </div>
    </div>
  );
}

// ─── Contacts tab (editable form) ──────────────────────────

function ContactsTab({ contacts: initial, dossierId, notes: initialNotes }: { contacts: Contact[]; dossierId: string; notes: ClientNote[] }) {
  const [contacts, setContacts] = useState<Contact[]>(initial);
  const [notes, setNotes] = useState<ClientNote[]>(initialNotes);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [addingContact, setAddingContact] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingData, setPendingData] = useState<Partial<Contact> | null>(null);
  const orgData = useDataStore((s) => s.orgData);
  // When the primary contact's client info changes and the dossier has an already-transferred
  // ordre, push the update to ServiceBox (updates the existing RDV's client, no duplicate).
  const [sbUpdateOrdre, setSbUpdateOrdre] = useState<Ordre | null>(null);

  const pushPrimaryEditToServiceBox = async () => {
    if (!orgData?.servicebox?.enabled || !orgData?.id) return;
    try {
      const { items } = await ordresApi.list({ org_id: orgData.id, dossier_id: dossierId });
      const transferred = (items || []).find((o) => o.servicebox_rdv_id);
      if (transferred) setSbUpdateOrdre(transferred);
    } catch { /* non-blocking */ }
  };

  useEffect(() => { setContacts(initial); }, [initial]);
  useEffect(() => { setNotes(initialNotes); }, [initialNotes]);

  const saveContacts = async (updated: Contact[]) => {
    setSaving(true);
    try {
      const result = await dossiersApi.update(dossierId, { contacts: updated } as any);
      if (result) {
        useDossierStore.getState().updateDossier(result);
        setContacts(updated);
      }
      showNotification({ type: "success", message: "Contact enregistré" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
    } finally {
      setSaving(false);
    }
  };

  const handleContactSave = (data: Partial<Contact>) => {
    if (editingContact) {
      if (editingContact.is_primary && (data.email !== editingContact.email || data.phone !== editingContact.phone)) {
        setPendingData(data);
        setShowConfirm(true);
        return;
      }
      applyContactSave(data);
    } else {
      const newContact: Contact = {
        id: `c-new-${Date.now()}`,
        dossier_id: dossierId,
        nom: data.nom || "",
        prenom: data.prenom,
        civilite: data.civilite,
        phone: data.phone,
        email: data.email,
        role: data.role,
        adresse: data.adresse,
        pays: data.pays || "FRANCE",
        is_primary: data.is_primary || false,
        created_at: new Date().toISOString(),
      };
      saveContacts([...contacts, newContact]);
      setAddingContact(false);
    }
  };

  const applyContactSave = async (data: Partial<Contact>) => {
    const target = editingContact!;
    const updated = contacts.map((c) => (c.id === target.id ? { ...c, ...data } : c));
    const clientChanged = (["adresse", "nom", "prenom", "phone", "email", "civilite"] as const).some(
      (k) => data[k] !== undefined && data[k] !== target[k],
    );
    setEditingContact(null);
    await saveContacts(updated);
    if (target.is_primary && clientChanged) pushPrimaryEditToServiceBox();
  };

  return (
    <div className="flex flex-col gap-3">
      <ClientNotesBanner
        dossierId={dossierId}
        notes={notes}
        onUpdated={(updated) => {
          setNotes(updated.client_notes || []);
          useDossierStore.getState().updateDossier(updated);
        }}
      />
      <div className="flex flex-row items-center justify-between">
        <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Contacts</span>
        <SecondaryButton variant="small" onClick={() => setAddingContact(true)}>
          <span className="flex flex-row items-center gap-1">
            <Icon1 name="plus" size={12} />
            Ajouter
          </span>
        </SecondaryButton>
      </div>
      {contacts.map((c) => {
        const displayName = [c.prenom, c.nom].filter(Boolean).join(" ") || "—";
        return (
          <div key={c.id} className="flex flex-row items-center gap-3 bg-white rounded-lg border border-neutral-200 px-3 py-2">
            <div className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999 }}>
              <Avvvatars value={displayName} style="shape" size={32} />
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              <span className="text-h3 font-medium" style={{ fontFamily: "Poppins" }}>
                {displayName}
                {c.is_primary && <span className="ml-2 text-h4 text-indigo-500 font-normal">Principal</span>}
              </span>
              <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>{c.role || "—"} · {c.phone || c.email || "—"}</span>
            </div>
            <button
              onClick={() => setEditingContact(c)}
              className="flex-shrink-0 text-h4 font-medium px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
              style={{ color: "#6366f1" }}
            >
              Modifier
            </button>
          </div>
        );
      })}
      {contacts.length === 0 && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun contact</span>
      )}

      {(editingContact || addingContact) && (
        <ContactFormModal
          contact={editingContact}
          onClose={() => { setEditingContact(null); setAddingContact(false); }}
          onSave={handleContactSave}
          saving={saving}
        />
      )}

      {showConfirm && (
        <ConfirmationModal
          title="Modifier le contact principal"
          message="Ce contact est utilisé pour les notifications WhatsApp et emails. Voulez-vous vraiment modifier ses coordonnées ?"
          confirmLabel="Oui, mettre à jour"
          onConfirm={() => { setShowConfirm(false); if (pendingData) applyContactSave(pendingData); setPendingData(null); }}
          onClose={() => { setShowConfirm(false); setPendingData(null); }}
        />
      )}

      {sbUpdateOrdre && orgData?.servicebox && (
        <ServiceBoxTransferModal
          autoRun
          ordre={sbUpdateOrdre}
          serviceboxConfig={orgData.servicebox}
          onClose={() => setSbUpdateOrdre(null)}
        />
      )}
    </div>
  );
}

// ─── Ordres tab (table) ────────────────────────────────────

function OrdresTab({ ordres }: { ordres: DossierOrdreSummary[] }) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-h3 font-medium text-neutral-500">
        Ordres de réparations liés ({ordres.length})
      </span>
      {ordres.length === 0 ? (
        <div className="text-h3 text-center text-neutral-400 py-8">Aucun ordre lié</div>
      ) : (
        <div className="border border-neutral-200 rounded-xl overflow-x-auto overflow-y-hidden">
          <table className="w-full" style={{ minWidth: 500 }}>
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                <th className="text-left text-h4 font-medium text-neutral-500 px-3 py-2">Dates</th>
                <th className="text-left text-h4 font-medium text-neutral-500 px-3 py-2">Ordre</th>
                <th className="text-left text-h4 font-medium text-neutral-500 px-3 py-2">Type</th>
                <th className="text-left text-h4 font-medium text-neutral-500 px-3 py-2">Véhicule</th>
                <th className="text-right text-h4 font-medium text-neutral-500 px-3 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {ordres.map((o) => (
                <tr key={o.id} className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => navigateTo({ route: `/ordre/${o.id}` })}>
                  <td className="text-h4 text-neutral-400 px-3 py-2.5">
                    {(() => {
                      const d1 = new Date(o.created_at);
                      const d2 = new Date(o.updated_at);
                      const fmt = (d: Date) => d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
                      const sameDay = d1.toDateString() === d2.toDateString();
                      return (
                        <>
                          <div>{fmt(d1)}</div>
                          <div className={sameDay ? "text-neutral-300" : ""}>{fmt(d2)}</div>
                        </>
                      );
                    })()}
                  </td>
                  <td className="text-h3 font-medium px-3 py-2.5">{o.num}</td>
                  <td className="text-h3 text-neutral-500 px-3 py-2.5">{o.type}</td>
                  <td className="text-h3 text-neutral-500 px-3 py-2.5">{o.vehicle_name}</td>
                  <td className="text-right px-3 py-2.5">
                    <span
                      className="text-h4 font-medium px-3 py-1 rounded-full"
                      style={{
                        color: o.status_color,
                        background: o.status_color + "18",
                      }}
                    >
                      {o.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Véhicules tab (compact list with edit modal) ──────────────

// Move a vehicle to another client (ownership change). The vehicle is spliced
// out of the current dossier and appended to the selected one; historical ordres
// stay with the previous owner, so the new client starts fresh.
function TransferVehicleModal({ vehicle, currentDossierId, onClose, onTransferred }: {
  vehicle: ClientVehicle;
  currentDossierId: string;
  onClose: () => void;
  onTransferred: (targetName: string) => void;
}) {
  const orgData = useDataStore((s) => s.orgData);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ProjectHit[]>([]);
  const [selected, setSelected] = useState<ProjectHit | null>(null);
  const [transferring, setTransferring] = useState(false);

  const handleSearch = async (q: string) => {
    setQuery(q);
    setSelected(null);
    if (!orgData?.id || q.trim().length < 2) {
      setResults([]);
      return;
    }
    try {
      const hits = await searchDossiersFull(q, orgData.id);
      setResults(hits.filter((h) => h.value !== currentDossierId));
    } catch {
      setResults([]);
    }
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setTransferring(true);
    try {
      await dossiersApi.transferVehicle(currentDossierId, vehicle.id, selected.value);
      showNotification({ type: "success", message: `Véhicule attribué à ${selected.label}` });
      onTransferred(selected.label);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'attribution du véhicule" });
    } finally {
      setTransferring(false);
    }
  };

  return (
    <BaseModal onClose={onClose} title="Attribuer à un autre client" width="max-w-lg" startIcon={<Icon1 name="car" size={20} />}>
      <div className="flex flex-col gap-4 p-4">
        {/* Vehicle being transferred */}
        <div className="flex flex-row items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2">
          <div className="w-14 h-9 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0">
            <VehicleImage marque={vehicle.marque} modele={vehicle.modele} coloris={vehicle.coloris} mec={vehicle.mec} alt={vehicle.plate} className="w-full h-full" iconSize={14} />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-h3 font-medium truncate">{vehicle.marque} {vehicle.modele}</span>
            <span className="text-h4 text-neutral-400">{vehicle.plate}</span>
          </div>
        </div>

        <TextField label="Nouveau client" value={query} onChange={handleSearch} placeholder="Rechercher un client…" />

        {results.length > 0 && !selected && (
          <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
            {results.map((r) => (
              <button
                key={r.value}
                onClick={() => setSelected(r)}
                className="flex flex-col items-start text-left px-3 py-2 rounded-lg hover:bg-neutral-50 border border-neutral-100"
              >
                <span className="text-h3 font-medium">{r.label}</span>
                {r.extra_label && <span className="text-h4 text-neutral-400">{r.extra_label}</span>}
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div className="flex flex-row items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-h3 font-medium truncate">{selected.label}</span>
              {selected.extra_label && <span className="text-h4 text-neutral-400 truncate">{selected.extra_label}</span>}
            </div>
            <button onClick={() => setSelected(null)} className="text-h4 font-medium text-neutral-400 hover:text-neutral-600">Changer</button>
          </div>
        )}

        <div className="flex flex-row justify-end gap-2">
          <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
          <Button
            variant="small"
            label={transferring ? "Attribution…" : "Attribuer le véhicule"}
            onClick={handleConfirm}
            disabled={!selected || transferring}
          />
        </div>
      </div>
    </BaseModal>
  );
}

function VehiculesTab({ vehicles: initial, dossierId, timeline, ordres, documents }: {
  vehicles: ClientVehicle[];
  dossierId: string;
  timeline: DossierTimelineEvent[];
  ordres: DossierOrdreSummary[];
  documents: DossierDocument[];
}) {
  const [vehicles, setVehicles] = useState<ClientVehicle[]>(initial);
  const [editVehicle, setEditVehicle] = useState<ClientVehicle | null>(null);
  const [transferVehicle, setTransferVehicle] = useState<ClientVehicle | null>(null);
  const [vehicleSaving, setVehicleSaving] = useState(false);

  // Sync with parent prop changes (e.g. after re-fetch)
  useEffect(() => { setVehicles(initial); }, [initial]);

  const saveVehicle = async (vehicle: ClientVehicle, data: Partial<ClientVehicle>) => {
    setVehicleSaving(true);
    try {
      const updatedVehicles = vehicles.map((v) => (v.id === vehicle.id ? { ...v, ...data } : v));
      setVehicles(updatedVehicles);
      await dossiersApi.update(dossierId, { vehicles: updatedVehicles } as any);
      // Mirror to the shared client_vehicles doc (same id post-repoint) so a fleet
      // car edited here reflects across every dossier. Best-effort.
      try { await clientVehiclesApi.update(vehicle.id, data); } catch { /* shared copy */ }
      setEditVehicle(null);
      showNotification({ type: "success", message: "Véhicule enregistré" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
    } finally {
      setVehicleSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-h3 font-semibold text-neutral-500">Véhicules du dossier</span>
      {vehicles.map((v) => (
        <div
          key={v.id}
          className="flex flex-row items-center gap-3 rounded-xl border border-neutral-200 bg-white px-4 py-3 transition-colors hover:border-neutral-300"
        >
          {/* Vehicle thumbnail */}
          <div className="w-16 h-10 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0">
            <VehicleImage marque={v.marque} modele={v.modele} coloris={v.coloris} mec={v.mec} alt={v.plate} className="w-full h-full" iconSize={16} />
          </div>

          {/* Vehicle info */}
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <span className="text-h3 font-medium truncate">
              {v.marque} {v.modele}
            </span>
            <span className="text-h4 text-neutral-400">{v.plate}{v.km ? ` · ${v.km.toLocaleString()} km` : ""}</span>
            {(() => {
              // Carte-grise parties overlaid from the shared client_vehicle.
              const parties = [
                { label: "Loueur", b: v.owner, color: "#f59e0b" },
                { label: "Société", b: v.company, color: "#10b981" },
                { label: "Conducteur", b: v.user, color: "#6366f1" },
              ].filter((p) => p.b && p.b.nom);
              if (parties.length === 0 && !v.is_fleet) return null;
              return (
                <div className="flex flex-row flex-wrap items-center gap-1.5">
                  {v.is_fleet && (
                    <span className="text-h5 font-medium px-1.5 py-0.5 rounded-md" style={{ color: "#0369a1", background: "#0369a118" }}>
                      Flotte
                    </span>
                  )}
                  {parties.map((p, i) => (
                    <span
                      key={i}
                      className="text-h5 font-medium px-1.5 py-0.5 rounded-md truncate max-w-[220px]"
                      style={{ color: p.color, background: p.color + "18" }}
                      title={`${p.label} : ${p.b!.nom}`}
                    >
                      {p.label} : {p.b!.nom}
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>

          <button
            onClick={() => setTransferVehicle(v)}
            className="flex-shrink-0 text-h4 font-medium px-2 py-1 rounded-lg hover:bg-neutral-100 transition-colors text-neutral-500"
          >
            Changer de client
          </button>
          <button
            onClick={() => setEditVehicle(v)}
            className="flex-shrink-0 text-h4 font-medium px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
            style={{ color: "#6366f1" }}
          >
            Modifier
          </button>
        </div>
      ))}

      {vehicles.length === 0 && (
        <div className="text-h3 text-center text-neutral-400 py-8">Aucun véhicule</div>
      )}

      {editVehicle && (
        <VehicleEditModal
          vehicle={editVehicle}
          onSave={(data) => saveVehicle(editVehicle, data)}
          onClose={() => setEditVehicle(null)}
          saving={vehicleSaving}
          timeline={timeline}
          ordres={ordres}
          documents={documents}
          dossierId={dossierId}
        />
      )}

      {transferVehicle && (
        <TransferVehicleModal
          vehicle={transferVehicle}
          currentDossierId={dossierId}
          onClose={() => setTransferVehicle(null)}
          onTransferred={() => {
            setVehicles((prev) => prev.filter((v) => v.id !== transferVehicle.id));
            setTransferVehicle(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Documents tab ─────────────────────────────────────────

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

function isImageDoc(d: { type: string; name: string; gcs_path?: string }) {
  return d.type === "image" || IMAGE_EXTENSIONS.test(d.gcs_path || d.name);
}

function DocPhotoThumbnail({ doc, onClick }: { doc: { id: string; name: string; gcs_path?: string }; onClick: () => void }) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!doc.gcs_path) return;
    const isOrdreDoc = doc.gcs_path.startsWith("ordres/");
    const parts = doc.gcs_path.split("/");
    const parentId = parts[2];
    const fetchUrl = async () => {
      try {
        const url = isOrdreDoc
          ? await ordresApi.getDocumentUrl(parentId, doc.id)
          : await dossiersApi.getDocumentUrl(parentId, doc.id);
        if (!cancelled) setThumbUrl(url);
      } catch { /* silently fail */ }
    };
    fetchUrl();
    return () => { cancelled = true; };
  }, [doc.gcs_path, doc.id]);

  return (
    <div
      className="w-[120px] h-[120px] rounded-lg overflow-hidden bg-neutral-100 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
      onClick={onClick}
    >
      {thumbUrl ? (
        <img src={thumbUrl} alt={doc.name} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <Icon1 name="image" size={24} color="#D4D4D4" />
      )}
    </div>
  );
}

interface DocSection {
  label: string;
  docs: Array<{ id: string; name: string; type: string; created_at?: string; gcs_path?: string }>;
}

function DocumentsTab({
  documents,
  ordreDocGroups,
  dossierId,
  onPreview,
  vehicles,
}: {
  documents: DossierDocument[];
  ordreDocGroups: { vehicleName: string; ordreNum: string; ordreId: string; docs: OrdreDocument[] }[];
  dossierId: string;
  onPreview: (doc: { id: string; name: string; type: string; gcs_path?: string; created_at?: string }) => void;
  vehicles: { id: string; marque?: string; modele?: string; plate?: string }[];
}) {
  const sections: DocSection[] = [];

  // Vehicle sections from ordres
  for (const g of ordreDocGroups) {
    if (g.docs.length > 0) {
      sections.push({ label: `${g.vehicleName} (${g.ordreNum})`, docs: g.docs });
    }
  }

  // Split dossier-level docs: vehicle-linked vs general
  const generalDocs: DossierDocument[] = [];
  const vehicleDocMap: Record<string, DossierDocument[]> = {};
  for (const d of documents) {
    const vid = d.vehicle_id;
    if (vid) {
      if (!vehicleDocMap[vid]) vehicleDocMap[vid] = [];
      vehicleDocMap[vid].push(d);
    } else {
      generalDocs.push(d);
    }
  }

  for (const [vid, docs] of Object.entries(vehicleDocMap)) {
    const v = vehicles.find((veh) => veh.id === vid);
    const vehicleName = v ? `${v.marque || ""} ${v.modele || ""}`.trim() || v.plate || "Véhicule" : "Véhicule";
    sections.push({ label: vehicleName, docs });
  }

  if (generalDocs.length > 0) {
    sections.push({ label: "Général", docs: generalDocs });
  }

  const totalDocs = sections.reduce((s, sec) => s + sec.docs.length, 0);

  if (totalDocs === 0) {
    return <div className="text-h3 text-center text-neutral-400 py-8">Aucun document</div>;
  }

  return (
    <div className="flex flex-col gap-6">
      {sections.map((sec) => {
        const photos = sec.docs.filter(isImageDoc);
        const files = sec.docs.filter((d) => !isImageDoc(d));
        return (
          <div key={sec.label} className="flex flex-col gap-2">
            {sections.length > 1 && (
              <span className="text-h3 font-semibold text-neutral-700">{sec.label}</span>
            )}
            {photos.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <span className="text-h4 text-neutral-400">Photos ({photos.length})</span>
                <div className="flex flex-row flex-wrap gap-2">
                  {photos.map((p) => (
                    <DocPhotoThumbnail key={p.id} doc={p} onClick={() => onPreview(p)} />
                  ))}
                </div>
              </div>
            )}
            {files.length > 0 && (
              <div className="flex flex-col gap-1">
                {photos.length > 0 && <span className="text-h4 text-neutral-400 mt-1">Documents ({files.length})</span>}
                {files.map((d) => (
                  <div
                    key={d.id}
                    className="flex flex-row items-center gap-3 p-3 border border-neutral-200 rounded-xl hover:bg-neutral-50 cursor-pointer"
                    onClick={() => onPreview(d)}
                  >
                    <div className="w-36 h-36 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                      <Icon1 name={d.type === "pdf" ? "file" : "image"} size={18} color="#A1A1A1" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-h3 font-medium truncate">{d.name}</span>
                      {d.created_at && <span className="text-h4 text-neutral-500">{d.created_at}</span>}
                    </div>
                    <Icon1 name="download" size={16} color="#A1A1A1" />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Add Document Modal ───────────────────────────────────

const DOCUMENT_TYPE_OPTIONS = [
  { value: "carte_grise", label: "Carte grise" },
  { value: "assurance", label: "Assurance" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "constat_amiable", label: "Constat amiable" },
  { value: "permis_conduire", label: "Permis de conduire" },
  { value: "devis", label: "Devis" },
  { value: "facture", label: "Facture" },
  { value: "bon_commande", label: "Bon de commande" },
  { value: "bon_livraison", label: "Bon de livraison" },
  { value: "contrat_location", label: "Contrat de location / prêt" },
  { value: "rapport_expertise", label: "Rapport d'expertise" },
  { value: "photo_vehicule", label: "Photo véhicule" },
  { value: "photo_degats", label: "Photo dégâts" },
  { value: "courrier", label: "Courrier" },
  { value: "autre", label: "Autre" },
];

function AddDocumentModal({
  dossierId,
  onClose,
  onSuccess,
}: {
  dossierId: string;
  onClose: () => void;
  onSuccess: (dossier: Dossier) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [classification, setClassification] = useState<DocumentClassification | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setDocName(selectedFile.name);

    // Auto-classify with AI
    setClassifying(true);
    try {
      const result = await dossiersApi.classifyDocument(selectedFile);
      setClassification(result);
      if (result.suggested_name) {
        // Preserve original file extension in the suggested name
        const origExt = selectedFile.name.includes(".") ? selectedFile.name.substring(selectedFile.name.lastIndexOf(".")) : "";
        const suggestedHasExt = result.suggested_name.includes(".");
        setDocName(suggestedHasExt ? result.suggested_name : result.suggested_name + origExt);
      }
      if (result.type_key) setDocType(result.type_key);
    } catch {
      // Classification failed — user can still manually select type
    } finally {
      setClassifying(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      let uploadFile = file;
      if (file.type.startsWith("image/")) {
        const { compressImage } = await import("@utils/compressImage");
        uploadFile = await compressImage(file);
      }
      const updated = await dossiersApi.uploadDocument(dossierId, uploadFile, docName || undefined, docType || undefined);
      showNotification({ type: "success", message: "Document ajouté" });
      onSuccess(updated);
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <BaseModal
      title="Ajouter un document"
      startIcon={<Icon1 name="upload" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Drop zone */}
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${
            dragOver ? "border-indigo-400 bg-indigo-50" : "border-neutral-300 hover:bg-neutral-50"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <Icon1 name="file" size={28} color="#6366f1" />
              <span className="text-h3 font-medium text-neutral-700 text-center">{file.name}</span>
              <span className="text-h4 text-neutral-400">{(file.size / 1024).toFixed(0)} Ko</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Icon1 name="upload" size={24} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Glisser ou cliquer pour importer</span>
              <span className="text-h4 text-neutral-300">PDF, images, documents</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileSelect(f);
            }}
          />
        </div>

        {/* AI classification result */}
        {classifying && (
          <div className="flex flex-row items-center gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
            <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-h3 text-indigo-600">Analyse du document en cours...</span>
          </div>
        )}
        {classification && !classifying && classification.confidence > 0.3 && (
          <div className="flex flex-row items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
            <Icon1 name="done" size={16} color="#10b981" />
            <span className="text-h3 text-emerald-700">
              Document identifié : <strong>{classification.type_label}</strong>
              {classification.confidence >= 0.7 ? "" : " (incertain)"}
            </span>
          </div>
        )}

        {/* Document name */}
        <TextField label="Nom du document" value={docName} onChange={setDocName} />

        {/* Document type dropdown */}
        <DropDown
          label="Type de document"
          options={DOCUMENT_TYPE_OPTIONS}
          value={docType}
          onSelect={(v) => setDocType(String(v))}
        />
      </div>

      {/* Footer */}
      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={uploading ? "Envoi..." : "Ajouter"}
          onClick={handleUpload}
          disabled={!file || uploading}
        />
      </div>
    </BaseModal>
  );
}

// ─── Document Preview Modal ───────────────────────────────
// Shared component — imported from @components/ui/DocumentPreviewModal

const CHAT_MEMBERS = [{ name: "M. Martin" }, { name: "Emeric" }, { name: "Mélanie" }];

// ─── Main screen ───────────────────────────────────────────

const CONTENT_TABS = [
  { value: "vehicules", label: "Véhicules" },
  { value: "contacts", label: "Contacts" },
  { value: "ordres", label: "Ordres" },
  { value: "documents", label: "Documents" },
  { value: "fil", label: "Fil du dossier" },
];

const TYPE_BADGE_STYLES = CLIENT_TYPE_BADGE;

export default function DossierDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const orgData = useDataStore((s) => s.orgData);
  const [activeTab, setActiveTab] = useState("vehicules");
  const [search, setSearch] = useState("");
  const [fetchedDossier, setFetchedDossier] = useState<Dossier | null>(null);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [showEditDossier, setShowEditDossier] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLinkedOrdres, setDeleteLinkedOrdres] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{ doc: { id: string; name: string; type: string; gcs_path?: string }; url: string } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [linkedOrdres, setLinkedOrdres] = useState<Ordre[]>([]);

  const handleDeleteDossier = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      if (deleteLinkedOrdres && orgData?.id) {
        const { items: linked } = await ordresApi.list({ org_id: orgData.id, dossier_id: id });
        for (const o of linked) {
          await ordresApi.delete(o.id);
        }
      }
      await dossiersApi.delete(id);
      useDossierStore.getState().removeDossiers([id]);
      showNotification({ type: "success", message: "Dossier supprimé" });
      navigateTo({ route: "/dossiers" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la suppression du dossier" });
    }
    setDeleting(false);
    setShowDeleteConfirm(false);
  };

  // Fetch fresh dossier data from API on mount
  useEffect(() => {
    if (!id) return;
    if (orgData?.id) useTagStore.getState().fetchTags(orgData.id);
    dossiersApi.get(id).then((d) => {
      setFetchedDossier(d);
    }).catch(() => {});
  }, [id, orgData?.id]);

  const dossier = useMemo(() => {
    if (fetchedDossier) return getEnrichedDossier(id || "") ? { ...getEnrichedDossier(id || "")!, ...fetchedDossier } : fetchedDossier;
    return getEnrichedDossier(id || "");
  }, [id, fetchedDossier]);

  // WhatsApp linked: org has WhatsApp connected + dossier has a phone number
  const whatsappLinked = !!(orgData?.whatsapp?.connected && dossier?.phone);

  // Fetch full ordres linked to this dossier (to get their documents)
  useEffect(() => {
    if (!id) return;
    ordresApi.list({ dossier_id: id }).then(({ items }) => {
      setLinkedOrdres(items);
    }).catch(() => {});
  }, [id]);

  const ordreDocGroups = useMemo(() => {
    return linkedOrdres
      .filter((o) => o.documents && o.documents.length > 0)
      .map((o) => {
        const v = o.vehicle;
        const vehicleName = v ? `${v.marque || ""} ${v.modele || ""}`.trim() || o.immatriculation || "Véhicule" : o.immatriculation || "Véhicule";
        return { vehicleName, ordreNum: o.num, ordreId: o.id, docs: o.documents || [] };
      });
  }, [linkedOrdres]);

  // Flat list of all docs (dossier + ordres) for timeline thumbnail resolution
  const allDocs: AllDocEntry[] = useMemo(() => {
    const entries: AllDocEntry[] = [];
    if (dossier) {
      for (const d of dossier.documents || []) {
        entries.push({ id: d.id, name: d.name, gcs_path: d.gcs_path, parentType: "dossier", parentId: dossier.id });
      }
    }
    for (const o of linkedOrdres) {
      for (const d of o.documents || []) {
        entries.push({ id: d.id, name: d.name, gcs_path: d.gcs_path, parentType: "ordre", parentId: o.id });
      }
    }
    return entries;
  }, [dossier, linkedOrdres]);

  if (!dossier) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: "#FAFAFA" }}>
        <span className="text-h2 text-neutral-400">Dossier introuvable</span>
      </div>
    );
  }

  const openDocPreview = async (doc: { id: string; name: string; type: string; gcs_path?: string }) => {
    if (!doc.gcs_path) return;
    setLoadingPreview(true);
    try {
      const isOrdreDoc = doc.gcs_path.startsWith("ordres/");
      if (isOrdreDoc) {
        const parts = doc.gcs_path.split("/");
        const ordreId = parts[2];
        const url = await ordresApi.getDocumentUrl(ordreId, doc.id);
        setPreviewDoc({ doc, url });
      } else {
        const url = await dossiersApi.getDocumentUrl(dossier.id, doc.id);
        setPreviewDoc({ doc, url });
      }
    } catch {
      showNotification({ type: "error", message: "Impossible de charger l'aperçu" });
    } finally {
      setLoadingPreview(false);
    }
  };

  const openDocPreviewByName = (docName: string) => {
    const doc = (dossier.documents || []).find((d) => d.name === docName);
    if (doc) { openDocPreview(doc); return; }
    // Search in linked ordres' documents
    for (const o of linkedOrdres) {
      const ordreDoc = (o.documents || []).find((d) => d.name === docName);
      if (ordreDoc) { openDocPreview(ordreDoc); return; }
    }
  };

  const badge = TYPE_BADGE_STYLES[dossier.type] || TYPE_BADGE_STYLES.particulier;

  return (
    <div className="flex flex-row h-screen overflow-hidden" style={{ background: "#FAFAFA" }}>
      {showDeleteConfirm && (
        <ConfirmationModal
          title="Supprimer ce dossier ?"
          message="Cette action est irréversible. Le dossier et toutes ses données seront définitivement supprimés."
          onClose={() => { setShowDeleteConfirm(false); setDeleteLinkedOrdres(false); }}
          onConfirm={handleDeleteDossier}
          confirmText="CONFIRMER"
          extraContent={
            <div className="mt-2">
              <CheckBoxInput
                checked={deleteLinkedOrdres}
                onChange={(e: any) => setDeleteLinkedOrdres(e.target.checked)}
                label="Supprimer aussi les ordres associés"
              />
            </div>
          }
        />
      )}
      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-y-auto min-w-0">
        {/* Header */}
        <div className="flex flex-row items-start justify-between px-6 pt-8 pb-4">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-3">
              <Text1>{dossier.name}</Text1>
              <span
                className="text-h4 font-medium rounded-full px-2.5 py-0.5"
                style={{ background: badge.bg, color: badge.text }}
              >
                {badge.label}
              </span>
            </div>
            <InlineTagEditor
              scope="dossier"
              tagIds={dossier.tag_ids || []}
              onSave={async (ids) => {
                try {
                  const updated = await dossiersApi.update(dossier.id, { tag_ids: ids });
                  useDossierStore.getState().updateDossier(updated);
                  setFetchedDossier((prev) => prev ? { ...prev, tag_ids: ids } : prev);
                } catch {}
              }}
            />
          </div>
          <MenuButton label="Actions">
            <div className="flex flex-col p-1">
              <SideMenuButton
                size="large"
                label="Modifier le dossier"
                name="modify"
                onClick={() => setShowEditDossier(true)}
              />
              <SideMenuButton
                size="large"
                label="Créer un ordre"
                name="side-ordres"
              />
              <SideMenuButton
                size="large"
                label="Ajouter un document"
                name="upload"
                onClick={() => setShowAddDoc(true)}
              />
              <SideMenuButton
                size="large"
                label="Supprimer"
                name="delete"
                color="#ef4444"
                onClick={() => setShowDeleteConfirm(true)}
              />
            </div>
          </MenuButton>
        </div>

        {/* Tabs */}
        <div className="px-6 overflow-hidden">
          <ChunkyTabs activeTab={activeTab} tabs={CONTENT_TABS} setActiveTab={setActiveTab} />
        </div>

        {/* Search */}
        <div className="px-6 pt-3" style={{ maxWidth: 500 }}>
          <SearchField value={search} onChange={setSearch} placeholder="Chercher .." />
        </div>

        {/* Tab content */}
        <div className="px-6 pt-4 pb-8 flex-1 overflow-x-hidden">
          {activeTab === "fil" && <FilDuDossierTab timeline={dossier.timeline || []} vehicles={dossier.vehicles || []} search={search} onDocClick={openDocPreviewByName} allDocs={allDocs} />}
          {activeTab === "contacts" && <ContactsTab contacts={dossier.contacts || []} dossierId={dossier.id} notes={dossier.client_notes || []} />}
          {activeTab === "ordres" && <OrdresTab ordres={dossier.ordres || []} />}
          {activeTab === "vehicules" && <VehiculesTab vehicles={dossier.vehicles || []} dossierId={dossier.id} timeline={dossier.timeline || []} ordres={dossier.ordres || []} documents={dossier.documents || []} />}
          {activeTab === "documents" && <DocumentsTab documents={dossier.documents || []} ordreDocGroups={ordreDocGroups} dossierId={dossier.id} onPreview={openDocPreview} vehicles={dossier.vehicles || []} />}
        </div>
      </div>

      {/* Right chat panel — full on 2xl screens */}
      <div className="flex-shrink-0 hidden 2xl:block" style={{ width: 400 }}>
        <ChatPanel
          discussionId={dossier.discussion_id}
          internalDiscussionId={dossier.internal_discussion_id}
          entityType="dossier"
          entityId={dossier.id}
          entityName={dossier.name}
          members={CHAT_MEMBERS}
          onlineCount={2}
          whatsappLinked={whatsappLinked}
          whatsappProvider={orgData?.whatsapp?.provider || "wasender"}
        />
      </div>

      {/* Floating chat widget — below xl only */}
      <FloatingChatWidget
        discussionId={dossier.discussion_id}
        internalDiscussionId={dossier.internal_discussion_id}
        entityType="dossier"
        entityId={dossier.id}
        entityName={dossier.name}
        whatsappLinked={whatsappLinked}
        whatsappProvider={orgData?.whatsapp?.provider || "wasender"}
      />

      {/* Edit dossier modal */}
      {showEditDossier && (
        <DossierEditModal
          dossier={dossier}
          onClose={() => setShowEditDossier(false)}
          onSuccess={(updated) => setFetchedDossier(updated)}
        />
      )}

      {/* Add document modal */}
      {showAddDoc && (
        <AddDocumentModal
          dossierId={dossier.id}
          onClose={() => setShowAddDoc(false)}
          onSuccess={(updated) => setFetchedDossier(updated)}
        />
      )}

      {/* Document preview modal */}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc.doc}
          url={previewDoc.url}
          onClose={() => setPreviewDoc(null)}
          onDownload={() => window.open(previewDoc.url, "_blank")}
        />
      )}
    </div>
  );
}
